import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTarget, FiTrendingUp, FiClock, FiUsers, FiCheckCircle, FiDollarSign, FiZap, FiShield } = FiIcons;

const ExecutiveSummary = () => {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Executive Summary: What We Built</h1>
        <p className="text-lg text-gray-600 max-w-3xl">
          We selected the <strong>Web Marketing Coordinator SOP & Workflow System</strong> to directly address the operational chaos often found in high-growth marketing teams.
        </p>
      </motion.div>

      {/* Key Pain Point Solved */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="glass-card rounded-xl p-8 mb-8 border-l-4 border-emerald-500"
      >
        <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
          <SafeIcon icon={FiTarget} className="w-6 h-6 text-emerald-600 mr-3" />
          The Core Problem Solved
        </h2>
        <p className="text-gray-700 text-lg leading-relaxed">
          Most marketing coordinators perform repetitive, high-volume tasks (web updates, email sends, reporting) without a safety net. This leads to <strong>inconsistent quality, slow onboarding, and simple errors</strong> that damage brand trust. 
          <br /><br />
          This system replaces "tribal knowledge" with documented, automated workflows.
        </p>
      </motion.div>

      {/* ROI and Users */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-card rounded-xl p-6"
        >
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
            <SafeIcon icon={FiTrendingUp} className="w-5 h-5 text-emerald-600 mr-2" />
            Expected ROI
          </h3>
          <ul className="space-y-4">
            <li className="flex items-start">
              <div className="w-12 text-emerald-600 font-bold">Time</div>
              <div className="text-gray-600">Saves 15-20 hours/week by automating reporting & simplified approvals.</div>
            </li>
            <li className="flex items-start">
              <div className="w-12 text-emerald-600 font-bold">Quality</div>
              <div className="text-gray-600">Reduces publishing errors by 90% via QA checklists.</div>
            </li>
            <li className="flex items-start">
              <div className="w-12 text-emerald-600 font-bold">Clarity</div>
              <div className="text-gray-600">Eliminates "what do I do next?" delays completely.</div>
            </li>
          </ul>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="glass-card rounded-xl p-6"
        >
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
            <SafeIcon icon={FiUsers} className="w-5 h-5 text-emerald-600 mr-2" />
            User & Onboarding Impact
          </h3>
          <div className="mb-4">
            <span className="font-semibold text-gray-900">Primary User:</span>
            <span className="text-gray-600 ml-2">Web Marketing Coordinator</span>
          </div>
          <div className="mb-4">
            <span className="font-semibold text-gray-900">Secondary Users:</span>
            <span className="text-gray-600 ml-2">Marketing Manager, Content Specialists</span>
          </div>
          <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-100">
            <span className="font-semibold text-emerald-800">Onboarding Speed:</span>
            <p className="text-sm text-emerald-700 mt-1">
              Reduces ramp-up time from <strong>3 months to 2 weeks</strong>. New hires simply follow the "Playbook" from Day 1.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ExecutiveSummary;